import { useEffect, useRef, useState } from "react";
import { Paintbrush, Eraser, Trash2, Download, Undo2, Upload } from "lucide-react";
import { saveArtwork } from "@/lib/api";
import { EMBED } from "@/constants/testIds";

const COLORS = ["#00F0FF", "#39FF14", "#FFE300", "#FF003C", "#FFFFFF", "#8A2BE2", "#FF8C00", "#000000"];

export default function JixxeoDraw() {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef([0, 0]);
  const [color, setColor] = useState("#00F0FF");
  const [size, setSize] = useState(6);
  const [eraser, setEraser] = useState(false);
  const historyRef = useRef([]);

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.clientWidth * dpr;
    c.height = c.clientHeight * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, c.clientWidth, c.clientHeight);
    ctxRef.current = ctx;
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const c = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return [p.clientX - c.left, p.clientY - c.top];
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
    // snapshot for undo
    try {
      const c = canvasRef.current;
      historyRef.current.push(ctxRef.current.getImageData(0, 0, c.width, c.height));
      if (historyRef.current.length > 15) historyRef.current.shift();
    } catch {}
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const [x, y] = pos(e);
    const ctx = ctxRef.current;
    ctx.strokeStyle = eraser ? "#050505" : color;
    ctx.lineWidth = eraser ? size * 3 : size;
    if (!eraser) {
      ctx.shadowColor = color;
      ctx.shadowBlur = 12;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.beginPath();
    ctx.moveTo(lastRef.current[0], lastRef.current[1]);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = [x, y];
  };

  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    ctxRef.current.fillStyle = "#050505";
    ctxRef.current.fillRect(0, 0, c.clientWidth, c.clientHeight);
    historyRef.current = [];
  };

  const undo = () => {
    const snap = historyRef.current.pop();
    if (snap) ctxRef.current.putImageData(snap, 0, 0);
  };

  const download = () => {
    const link = document.createElement("a");
    link.download = `jixxeo-draw-${Date.now()}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const saveToGallery = async () => {
    if (saving) return;
    setSaving(true);
    setSavedMsg("");
    try {
      // downscale to keep payload small
      const src = canvasRef.current;
      const maxW = 900;
      const scale = Math.min(1, maxW / src.width);
      const w = Math.round(src.width * scale);
      const h = Math.round(src.height * scale);
      const off = document.createElement("canvas");
      off.width = w; off.height = h;
      const octx = off.getContext("2d");
      octx.drawImage(src, 0, 0, w, h);
      const dataUrl = off.toDataURL("image/png");
      const title = window.prompt("Title this piece:", "Untitled");
      if (title === null) { setSaving(false); return; }
      await saveArtwork(title || "Untitled", dataUrl);
      setSavedMsg("Saved to gallery ✓");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setSavedMsg("Sign in to save");
      setTimeout(() => setSavedMsg(""), 2500);
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-3 md:p-6 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-9 h-9 rounded-lg bg-[#00F0FF] flex items-center justify-center">
          <Paintbrush className="w-5 h-5 text-black" strokeWidth={3} />
        </span>
        <div>
          <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
          <h1 className="font-heading font-black text-2xl uppercase tracking-tighter leading-none">Jixxeo Draw</h1>
        </div>
        <div className="ml-auto flex gap-1 flex-wrap">
          <ToolBtn onClick={undo}><Undo2 className="w-4 h-4" /></ToolBtn>
          <ToolBtn onClick={() => setEraser((e) => !e)} active={eraser}><Eraser className="w-4 h-4" /></ToolBtn>
          <ToolBtn onClick={clear}><Trash2 className="w-4 h-4" /></ToolBtn>
          <ToolBtn onClick={saveToGallery} accent="#00F0FF" testid={EMBED.drawSave}>
            <Upload className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={download} accent="#39FF14"><Download className="w-4 h-4" /></ToolBtn>
        </div>
      </div>
      {savedMsg && (
        <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">{saving ? "Saving…" : savedMsg}</div>
      )}

      <div className="flex gap-1.5 items-center mb-2 flex-wrap">
        {COLORS.map((c) => (
          <button key={c} onClick={() => { setColor(c); setEraser(false); }}
            className="w-8 h-8 rounded-lg border-2 transition-transform hover:-translate-y-0.5"
            style={{ background: c, borderColor: color === c && !eraser ? "#00F0FF" : "rgba(255,255,255,0.15)" }} />
        ))}
        <input type="range" min="2" max="30" value={size} onChange={(e) => setSize(+e.target.value)}
          className="ml-2 flex-1 max-w-[140px] accent-[#00F0FF]" />
        <span className="mono text-[10px] text-white/50">{size}px</span>
      </div>

      <canvas
        ref={canvasRef}
        className="flex-1 rounded-2xl border border-white/10 bg-[#050505] cursor-crosshair touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-2 text-center">
        neon canvas · web api · runs entirely in browser
      </div>
    </div>
  );
}

function ToolBtn({ children, onClick, active, accent, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-9 h-9 rounded-lg border flex items-center justify-center transition-colors"
      style={{
        borderColor: active ? "#FFE300" : "rgba(255,255,255,0.1)",
        background: active ? "#FFE30015" : "transparent",
        color: active ? "#FFE300" : accent || "#fff",
      }}>
      {children}
    </button>
  );
}
